import { Feature, Geometry, GeoJsonProperties } from 'geojson';
import { BaseMapController } from '../core/BaseMapController';
import { ILineConfig } from '../types';
import { GeometryUtils } from '../utils/GeometryUtils';

/**
 * LineManager - handles all line-related functionality
 * - Adding/removing/updating lines
 * - Line rendering and styling
 * - State management
 */
export class LineManager {
  private mapController: BaseMapController;
  private lines: ILineConfig[] = [];

  constructor(mapController: BaseMapController) {
    this.mapController = mapController;
  }

  addLine(line: ILineConfig): void {
    try {
      const existingLine = this.lines.find(l => l.id === line.id);
      if (existingLine) {
        this.updateLine(line);
        return;
      }

      this.lines.push(line);

      const lineData: Feature<Geometry, GeoJsonProperties> = {
        type: 'Feature',
        properties: { id: line.id },
        geometry: {
          type: 'LineString',
          coordinates: line.coordinates.map(coord => [coord.lng, coord.lat]),
        },
      };

      if (this.mapController.getMap().getSource(`line-${line.id}`)) {
        this.removeLine(line.id);
        this.lines.push(line);
      }

      this.mapController.getMap().addSource(`line-${line.id}`, {
        type: 'geojson',
        data: lineData,
      });

      this.mapController.getMap().addLayer({
        id: `line-${line.id}`,
        type: 'line',
        source: `line-${line.id}`,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': line.color || '#4ecdc4',
          'line-width': line.width || 3,
          'line-opacity': line.opacity || 1,
        },
      });
    } catch (error) {
      console.error('Error adding line:', error);
    }
  }

  updateLine(line: ILineConfig): void {
    try {
      const existingLineIndex = this.lines.findIndex(l => l.id === line.id);

      if (existingLineIndex === -1) {
        this.addLine(line);
        return;
      }

      this.lines[existingLineIndex] = line;

      const lineData: Feature<Geometry, GeoJsonProperties> = {
        type: 'Feature',
        properties: { id: line.id },
        geometry: {
          type: 'LineString',
          coordinates: line.coordinates.map(coord => [coord.lng, coord.lat]),
        },
      };

      const source = this.mapController
        .getMap()
        .getSource(`line-${line.id}`) as mapboxgl.GeoJSONSource;

      if (source) {
        source.setData(lineData);
      } else {
        this.addLine(line);
      }
    } catch (error) {
      console.error('Error updating line:', error);
      this.addLine(line);
    }
  }

  removeLine(lineId: string): void {
    try {
      this.lines = this.lines.filter(l => l.id !== lineId);

      const map = this.mapController.getMap();

      if (map.getLayer(`line-${lineId}`)) {
        map.removeLayer(`line-${lineId}`);
      }
      if (map.getSource(`line-${lineId}`)) {
        map.removeSource(`line-${lineId}`);
      }
    } catch (error) {
      console.error('Error removing line:', error);
    }
  }

  getLines(): ILineConfig[] {
    return [...this.lines];
  }

  hasLine(id: string): boolean {
    return this.lines.some(line => line.id === id);
  }

  getLine(lineId: string): ILineConfig | undefined {
    return this.lines.find(l => l.id === lineId);
  }

  clearLines(): void {
    try {
      this.lines.forEach(line => this.removeLine(line.id));
      this.lines = [];
    } catch (error) {
      console.error('Error clearing lines:', error);
    }
  }

  updateLineStyle(
    lineId: string,
    style: Partial<Pick<ILineConfig, 'color' | 'width' | 'opacity'>>
  ): void {
    try {
      const line = this.lines.find(l => l.id === lineId);
      if (!line) {
        console.warn(`Line with id "${lineId}" not found`);
        return;
      }

      if (style.color !== undefined) line.color = style.color;
      if (style.width !== undefined) line.width = style.width;
      if (style.opacity !== undefined) line.opacity = style.opacity;

      const map = this.mapController.getMap();
      const layerId = `line-${lineId}`;

      if (map.getLayer(layerId)) {
        if (style.color !== undefined) {
          map.setPaintProperty(layerId, 'line-color', style.color);
        }
        if (style.width !== undefined) {
          map.setPaintProperty(layerId, 'line-width', style.width);
        }
        if (style.opacity !== undefined) {
          map.setPaintProperty(layerId, 'line-opacity', style.opacity);
        }
      }
    } catch (error) {
      console.error('Error updating line style:', error);
    }
  }

  getStats(): {
    total: number;
    totalLength: number;
    averageLength: number;
  } {
    const totalLength = this.lines.reduce((sum, line) => {
      return sum + GeometryUtils.calculateLineLength(line.coordinates);
    }, 0);

    return {
      total: this.lines.length,
      totalLength,
      averageLength: this.lines.length > 0 ? totalLength / this.lines.length : 0,
    };
  }

  getLinesByColor(color: string): ILineConfig[] {
    return this.lines.filter(line => line.color === color);
  }

  getLinesInBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): ILineConfig[] {
    return this.lines.filter(line => {
      return line.coordinates.some(coord => {
        return (
          coord.lat <= bounds.north &&
          coord.lat >= bounds.south &&
          coord.lng <= bounds.east &&
          coord.lng >= bounds.west
        );
      });
    });
  }
}
